import { useRef } from "react";
import { Control, Controller, Path } from "react-hook-form";

import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import { DesktopDatePicker } from "@mui/x-date-pickers";

import { isValid, parse } from "date-fns";

type AppointmentDateFieldProps = {
  required?: boolean;
  name: Path<any>;
  control: Control<any, object>;
  hidePicker: () => void;
  sx?: SxProps;
  disabled?: boolean;
  label?: string;
  maxDate?: Date;
};

const DATE_MASK = "MM/dd/yyyy";

export default function AppointmentDateField({
  control,
  name,
  sx,
  required = true,
  disabled = false,
  hidePicker,
  label,
  maxDate
}: AppointmentDateFieldProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Box sx={{ maxWidth: 165, ...sx }}>
      <Controller
        name={name}
        control={control}
        rules={{
          ...(required
            ? {
                required: "Date is required"
              }
            : {}),
          validate: (value) => {
            const date =
              value instanceof Date
                ? value
                : value && parse(value, DATE_MASK, new Date());
            if (date && !isValid(date)) {
              return `The Date is not valid (${DATE_MASK}).`;
            }
            return true;
          }
        }}
        render={({
          field: { onBlur, name, onChange, ...field },
          fieldState
        }) => (
          <DesktopDatePicker
            {...field}
            views={["year", "month", "day"]}
            disabled={disabled}
            reduceAnimations={true}
            onChange={(newValue: any) => {
              if (isValid(newValue)) {
                onChange(newValue);
              }
            }}
            onAccept={(newValue: any) => {
              if (isValid(newValue)) {
                onChange(newValue);
                onBlur();
              }
            }}
            maxDate={maxDate}
            slotProps={{
              textField: {
                autoFocus: false,
                required,
                disabled,
                label,
                onBlur,
                onClick: hidePicker,
                name,
                InputProps: {
                  sx: { input: { px: "0" } }
                },
                inputRef,
                sx: { padding: "0 !important" },
                error: Boolean(fieldState.error),
                helperText: fieldState.error?.message
              }
            }}
            closeOnSelect
            format={DATE_MASK}
          />
        )}
      />
    </Box>
  );
}
